import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { actionTypes } from 'Types';
import type { DeviceType, LearningMode, PlayerLayer, SettingContext } from './settingTypes';

/**
 * The layers that have no store of their own in today's code (most settings'
 * mode defaults, a piece's transposition, a per-mode zoom), and the account
 * store's bookkeeping for every setting it keeps.
 *
 * - `values`: the player's value at each such layer. Redux-persisted, so it is
 *   this device's copy (the offline cache) of what the account store holds.
 * - `synced`: the value the account store last confirmed, per layer.
 * - `pending`: changes not yet confirmed by the account store. Persisted, so a
 *   change made offline is sent at the next sign-in or change.
 * - `refused`: changes the account store refused for good (a 400 or 422 for
 *   that value alone: a piece that no longer exists, a value it does not
 *   take). Set aside so they cannot block every later save. This device keeps
 *   using the value; it is not sent again unless the player changes it.
 *
 * Everything is cleared at sign-out: the account keeps the values, and the
 * next account starts from its own. Values are kept raw and read back through
 * each setting's `parse`.
 */

/** One value's place: the setting, the layer, and what the layer is for. */
export interface SettingCoordinate {
  key: string;
  layer: PlayerLayer;
  /** The learning mode, for a mode or instance value. */
  mode?: LearningMode;
  /** The piece, exercise or session, for an instance value. */
  instanceId?: string;
  /** The kind of device, for a setting kept per device type. */
  deviceType?: DeviceType;
}

export interface SettingCoordinateValue {
  coordinate: SettingCoordinate;
  value: unknown;
}

/** A change the account store refused for good, kept on this device only. */
export interface RefusedSettingWrite extends SettingCoordinateValue {
  /** The HTTP status the account store answered with. */
  status: number;
  /** When it was refused (ISO 8601). */
  refusedAt: string;
}

export interface LoadedSettingValue extends SettingCoordinateValue {
  /** True when the value has a store of its own on the device (a binding) and is put there. */
  bound: boolean;
}

export interface SettingsLayersState {
  values: Record<string, unknown>;
  synced: Record<string, unknown>;
  pending: Record<string, SettingCoordinateValue>;
  /** Optional: a copy persisted before it existed has none. */
  refused?: Record<string, RefusedSettingWrite>;
}

export const initialSettingsLayersState: SettingsLayersState = {
  values: {},
  synced: {},
  pending: {},
  refused: {},
};

/** The text a coordinate is kept under. */
export const coordinateId = ({
  key,
  layer,
  mode,
  instanceId,
  deviceType,
}: SettingCoordinate): string =>
  [key, layer, mode ?? '', instanceId ?? '', deviceType ?? ''].join('|');

const requireMode = (key: string, layer: PlayerLayer, context: SettingContext): LearningMode => {
  if (!context.mode) throw new Error(`A ${layer} value of ${key} needs a mode`);
  return context.mode;
};

const requireInstanceId = (key: string, context: SettingContext): string => {
  if (!context.instanceId) throw new Error(`An instance value of ${key} needs an instance id`);
  return context.instanceId;
};

/**
 * The coordinate of one layer of a setting, from what the context names.
 * `deviceType` is set only for a setting kept per device type.
 */
export const layerCoordinate = (
  key: string,
  layer: PlayerLayer,
  context: SettingContext,
): SettingCoordinate => {
  const deviceType = context.deviceType ? { deviceType: context.deviceType } : {};
  switch (layer) {
    case 'global':
      return { key, layer, ...deviceType };
    case 'mode':
      return { key, layer, mode: requireMode(key, layer, context), ...deviceType };
    case 'instance':
      return {
        key,
        layer,
        mode: requireMode(key, layer, context),
        instanceId: requireInstanceId(key, context),
        ...deviceType,
      };
    default:
      throw new Error(`Unknown setting layer: ${String(layer)}`);
  }
};

export interface SetSettingLayerValuePayload {
  key: string;
  layer: PlayerLayer;
  context: SettingContext;
  value: unknown;
}

const withoutKeys = <T>(
  record: Record<string, T> | undefined,
  keys: readonly string[],
): Record<string, T> =>
  Object.fromEntries(Object.entries(record ?? {}).filter(([id]) => !keys.includes(id)));

const settingsLayersSlice = createSlice({
  name: 'settingsLayers',
  initialState: initialSettingsLayersState,
  reducers: {
    settingLayerValueSet(
      state,
      action: PayloadAction<SetSettingLayerValuePayload>,
    ): SettingsLayersState {
      const { key, layer, context, value } = action.payload;
      const id = coordinateId(layerCoordinate(key, layer, context));
      return { ...state, values: { ...state.values, [id]: value } };
    },
    /** A value the player cleared, so the layer falls through again (a new session). */
    settingLayerValueCleared(
      state,
      action: PayloadAction<Omit<SetSettingLayerValuePayload, 'value'>>,
    ): SettingsLayersState {
      const { key, layer, context } = action.payload;
      const id = coordinateId(layerCoordinate(key, layer, context));
      return { ...state, values: withoutKeys(state.values, [id]) };
    },
    /**
     * The account store's values, at sign-in. A value with a store of its own is
     * put there by the sync (`bound`); the rest are kept here. A change still
     * waiting to be sent, or one the account refused, keeps the player's value.
     */
    accountSettingsLoaded(
      state,
      action: PayloadAction<{ values: LoadedSettingValue[] }>,
    ): SettingsLayersState {
      const loaded = action.payload.values.map((entry) => ({
        ...entry,
        id: coordinateId(entry.coordinate),
      }));
      const refused = state.refused ?? {};
      const unbound = loaded.filter(
        ({ id, bound }) => !bound && !(id in state.pending) && !(id in refused),
      );
      return {
        ...state,
        synced: {
          ...state.synced,
          ...Object.fromEntries(loaded.map(({ id, value }) => [id, value])),
        },
        values: {
          ...state.values,
          ...Object.fromEntries(unbound.map(({ id, value }) => [id, value])),
        },
      };
    },
    /** Player changes to send to the account store. A new value gets a fresh chance. */
    accountSettingsQueued(
      state,
      action: PayloadAction<{ writes: SettingCoordinateValue[] }>,
    ): SettingsLayersState {
      const queued = action.payload.writes.map((write) => ({
        write,
        id: coordinateId(write.coordinate),
      }));
      return {
        ...state,
        pending: {
          ...state.pending,
          ...Object.fromEntries(queued.map(({ id, write }) => [id, write])),
        },
        refused: withoutKeys(
          state.refused,
          queued.map(({ id }) => id),
        ),
      };
    },
    /**
     * Changes the account store confirmed. A pending change made after the write
     * was sent (a different value) stays pending.
     */
    accountSettingsConfirmed(
      state,
      action: PayloadAction<{ writes: SettingCoordinateValue[] }>,
    ): SettingsLayersState {
      const confirmed = action.payload.writes.map((write) => ({
        ...write,
        id: coordinateId(write.coordinate),
      }));
      const settled = confirmed
        .filter(({ id, value }) => state.pending[id]?.value === value)
        .map(({ id }) => id);
      return {
        ...state,
        synced: {
          ...state.synced,
          ...Object.fromEntries(confirmed.map(({ id, value }) => [id, value])),
        },
        pending: withoutKeys(state.pending, settled),
        refused: withoutKeys(
          state.refused,
          confirmed.map(({ id }) => id),
        ),
      };
    },
    /**
     * Changes the account store refused for good. Each is set aside: no longer
     * sent, kept on this device. A pending change made after the write was sent
     * (a different value) stays pending and gets its own try.
     */
    accountSettingsRefused(
      state,
      action: PayloadAction<{
        writes: SettingCoordinateValue[];
        status: number;
        refusedAt: string;
      }>,
    ): SettingsLayersState {
      const { writes, status, refusedAt } = action.payload;
      const refused = writes
        .map((write) => ({ ...write, id: coordinateId(write.coordinate) }))
        .filter(({ id, value }) => state.pending[id]?.value === value);
      return {
        ...state,
        pending: withoutKeys(
          state.pending,
          refused.map(({ id }) => id),
        ),
        refused: {
          ...(state.refused ?? {}),
          ...Object.fromEntries(
            refused.map(({ id, coordinate, value }) => [
              id,
              { coordinate, value, status, refusedAt },
            ]),
          ),
        },
      };
    },
    settingsLayersCleared(): SettingsLayersState {
      return initialSettingsLayersState;
    },
  },
  extraReducers: (builder) => {
    // Sign-out clears the app's data; the next account starts from its own values.
    builder.addMatcher(
      (action): boolean => action.type === actionTypes.CLEAR_DATA,
      (): SettingsLayersState => initialSettingsLayersState,
    );
  },
});

export const {
  settingLayerValueSet,
  settingLayerValueCleared,
  accountSettingsLoaded,
  accountSettingsQueued,
  accountSettingsConfirmed,
  accountSettingsRefused,
  settingsLayersCleared,
} = settingsLayersSlice.actions;

export const settingsLayersReducer = settingsLayersSlice.reducer;

type WithSettingsLayers = { settingsLayersReducer?: SettingsLayersState };

/** The raw value kept at one coordinate, or undefined when none is kept. */
export const selectSettingLayerValue = (
  state: WithSettingsLayers,
  coordinate: SettingCoordinate,
): unknown => state.settingsLayersReducer?.values?.[coordinateId(coordinate)];

/** The value the account store last confirmed at one coordinate. */
export const selectSyncedSettingValue = (
  state: WithSettingsLayers,
  coordinate: SettingCoordinate,
): unknown => state.settingsLayersReducer?.synced?.[coordinateId(coordinate)];

/** The changes still to send to the account store. */
export const selectPendingSettingWrites = (state: WithSettingsLayers): SettingCoordinateValue[] =>
  Object.values(state.settingsLayersReducer?.pending ?? {});

/** The changes the account store refused for good, kept on this device only. */
export const selectRefusedSettingWrites = (state: WithSettingsLayers): RefusedSettingWrite[] =>
  Object.values(state.settingsLayersReducer?.refused ?? {});

/** The refused change at one coordinate, if there is one. */
export const selectRefusedSettingWrite = (
  state: WithSettingsLayers,
  coordinate: SettingCoordinate,
): RefusedSettingWrite | undefined =>
  state.settingsLayersReducer?.refused?.[coordinateId(coordinate)];
